/**
 * The Activities catalog: one typed entry per activity. This file (plus
 * runs.ts/runsSummary.ts) is the platform's ONLY public surface; everything
 * under lib/activities/<slug>/ and app/activities/<slug>/ is private to that
 * activity, enforced by ESLint (see README.md in this folder).
 *
 * Adding an activity = one entry here + its two folders + its own tables.
 * Destroying one = delete the entry and follow README.md's runbook.
 */

export type ActivitySlug =
  | "scenarios"
  | "teach-back"
  | "voice-capsule"
  | "phrase-chunks"
  | "false-cognates"
  | "conjugation-gym"
  | "collocations"
  | "delayed-recall"
  | "minimal-pairs"
  | "spoken-numbers"
  | "shadowing"
  | "reading-fluency"
  | "predictive-listening"
  | "speed-listening"
  | "real-speech"
  | "message-decoder"
  | "circumlocution"
  | "word-order"
  | "grammar-forks";

/**
 * Target locales whose teach-back corpus has been hand-authored. Inlined rather
 * than read from the activity's content module: the registry must not reach
 * into activity internals (that is the whole point of the boundary). A
 * colocated test in the activity pins this set to the corpus, so the two cannot
 * drift silently.
 */
const TEACH_BACK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored anchor passage. Same boundary reasoning as above. */
const VOICE_CAPSULE_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored chunk corpus. Same boundary reasoning as above. */
const PHRASE_CHUNK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored false-cognate corpus. Same boundary reasoning. */
const FALSE_COGNATE_LOCALES = new Set(["pt-BR"]);

/** Locales with hand-checked verb tables. Same boundary reasoning. */
const CONJUGATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored collocation corpus. Same boundary reasoning. */
const COLLOCATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored contrast corpus. Same boundary reasoning. */
const MINIMAL_PAIR_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored shadowing corpus. Same boundary reasoning as above. */
const SHADOWING_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored concept corpus. Same boundary reasoning as above. */
const CIRCUMLOCUTION_LOCALES = new Set(["pt-BR"]);

/**
 * Locales with a hand-authored word-order corpus. Same boundary reasoning, and
 * here the corpus is the whole activity: the ordering rules it drills (proclisis,
 * the meaning-bearing adjective slot) are facts about Brazilian Portuguese and
 * about nothing else. A colocated test pins this set to the corpus.
 */
const WORD_ORDER_LOCALES = new Set(["pt-BR"]);

/**
 * Locales whose numbers this app can actually SPEAK.
 *
 * Unlike its neighbours, this set does not gate a corpus - spoken-numbers has none, it
 * generates every item. It gates the number speller: `numbers.ts` knows how to say
 * "mil duzentos e cinquenta" and nothing else. Adding a locale here without teaching
 * that module its number words would ship a drill that reads Portuguese words at a
 * learner of another language. A colocated test pins this set to the speller.
 */
const SPOKEN_NUMBER_LOCALES = new Set(["pt-BR"]);

/** Locales with a hand-authored spoken-to-written corpus. Same boundary reasoning. */
const REAL_SPEECH_LOCALES = new Set(["pt-BR"]);

/**
 * Locales with an authored message corpus. Same boundary reasoning as above, and it
 * bites harder here: the corpus is 37 hand-read messages of real chat Portuguese,
 * and nothing about it generalises to another language. Textspeak and the pragmatic
 * suffix layer are locale-specific by construction.
 */
const MESSAGE_DECODER_LOCALES = new Set(["pt-BR"]);

/**
 * Locales with a hand-authored, ambiguity-audited fork corpus. Same boundary
 * reasoning as above, and the audit is why this cannot be a wildcard: an item
 * whose other option a native speaker could defend marks a learner wrong for
 * correct Portuguese. Every sentence in the corpus was read back asking exactly
 * that, and a colocated test pins this set to what survived.
 */
const GRAMMAR_FORK_LOCALES = new Set(["pt-BR"]);

/**
 * Each activity owns one palette color. Seven colors, nineteen activities, so colors
 * are shared heavily: teach-back with delayed-recall (both are memory work),
 * phrase-chunks with collocations (both are about which words go together),
 * voice-capsule with shadowing (both are the learner's own voice), the three ear
 * activities (minimal-pairs, spoken-numbers, speed-listening) all take listening, and
 * `writing` now covers five. Sharers sit apart in the 2-column grid, so two cards of
 * the same color never touch - which is a constraint on registry ORDER, not just on
 * the color.
 *
 * ORDER IS LOAD-BEARING, AND IT IS TESTED AS A WHOLE. Two cards touch when they are
 * the two halves of one row (indices 2k and 2k+1) or one sits directly above the
 * other (i and i+2); diagonals are the arrangement sharers are meant to use. The
 * invariant is enforced across the entire list by
 * `test/lib/activities/accents.test.ts`, NOT by each activity checking its own
 * neighbours - that is how five `writing` cards ended up bunched at the end of the
 * catalog with three pairs touching and nothing failing. Appending a new activity at
 * the end is therefore not always safe: if it clashes, insert it where it does not.
 */
export type ActivityAccent =
  "coral" | "reading" | "writing" | "speaking" | "listening" | "lavender" | "recording";

export type ActivityDefinition = {
  slug: ActivitySlug;
  /** i18n key; copy never lives inline. */
  titleKey: string;
  /** i18n key; copy never lives inline. */
  descriptionKey: string;
  /** Material Symbol name; must be in the app/layout.tsx preload list. */
  icon: string;
  /** Palette color for this activity's card and page. */
  accent: ActivityAccent;
  href: string;
  /** "retired" = soft-kill: hidden from the catalog, code and data remain. */
  status: "active" | "retired";
  /** True when usable content exists for this target locale. */
  availableFor: (targetLocale: string) => Promise<boolean>;
};

export const ACTIVITIES: Record<ActivitySlug, ActivityDefinition> = {
  scenarios: {
    slug: "scenarios",
    titleKey: "activities.scenarios.title",
    descriptionKey: "activities.scenarios.description",
    icon: "forum",
    accent: "coral", // the flagship; conversation is the app's primary color
    href: "/activities/scenarios",
    status: "active",
    // Scenario content is generated at runtime for any locale.
    availableFor: async () => true,
  },
  "teach-back": {
    slug: "teach-back",
    titleKey: "activities.teach-back.title",
    descriptionKey: "activities.teach-back.description",
    icon: "school",
    accent: "lavender",
    href: "/activities/teach-back",
    status: "active",
    availableFor: async (targetLocale) => TEACH_BACK_LOCALES.has(targetLocale),
  },
  "voice-capsule": {
    slug: "voice-capsule",
    titleKey: "activities.voice-capsule.title",
    descriptionKey: "activities.voice-capsule.description",
    icon: "mic",
    accent: "speaking", // it is literally the speaking skill
    href: "/activities/voice-capsule",
    status: "active",
    availableFor: async (targetLocale) => VOICE_CAPSULE_LOCALES.has(targetLocale),
  },
  "phrase-chunks": {
    slug: "phrase-chunks",
    titleKey: "activities.phrase-chunks.title",
    descriptionKey: "activities.phrase-chunks.description",
    icon: "exercise",
    accent: "reading",
    href: "/activities/phrase-chunks",
    status: "active",
    availableFor: async (targetLocale) => PHRASE_CHUNK_LOCALES.has(targetLocale),
  },
  "false-cognates": {
    slug: "false-cognates",
    titleKey: "activities.false-cognates.title",
    descriptionKey: "activities.false-cognates.description",
    icon: "psychology",
    accent: "recording", // Danger Zone. The copy says these words "betray you".
    href: "/activities/false-cognates",
    status: "active",
    availableFor: async (targetLocale) => FALSE_COGNATE_LOCALES.has(targetLocale),
  },
  "conjugation-gym": {
    slug: "conjugation-gym",
    titleKey: "activities.conjugation-gym.title",
    descriptionKey: "activities.conjugation-gym.description",
    icon: "exercise",
    accent: "writing",
    href: "/activities/conjugation-gym",
    status: "active",
    availableFor: async (targetLocale) => CONJUGATION_LOCALES.has(targetLocale),
  },
  "minimal-pairs": {
    slug: "minimal-pairs",
    titleKey: "activities.minimal-pairs.title",
    descriptionKey: "activities.minimal-pairs.description",
    icon: "hearing",
    // Ear Training is the listening skill, so it takes the listening color outright.
    accent: "listening",
    href: "/activities/minimal-pairs",
    status: "active",
    availableFor: async (targetLocale) => MINIMAL_PAIR_LOCALES.has(targetLocale),
  },
  "delayed-recall": {
    slug: "delayed-recall",
    titleKey: "activities.delayed-recall.title",
    descriptionKey: "activities.delayed-recall.description",
    icon: "visibility_off",
    accent: "lavender", // the deliberate reuse; see ActivityAccent
    href: "/activities/delayed-recall",
    status: "active",
    // No corpus: the passage is generated per session, so any target locale the
    // model writes works. Same reasoning as scenarios, and the reason this is
    // the only activity since scenarios with no *_LOCALES set above.
    availableFor: async () => true,
  },
  collocations: {
    slug: "collocations",
    titleKey: "activities.collocations.title",
    descriptionKey: "activities.collocations.description",
    icon: "assignment",
    // Shares reading with phrase-chunks: both are about which words go together. The
    // listening color went to Ear Training, which is that skill outright.
    accent: "reading",
    href: "/activities/collocations",
    status: "active",
    availableFor: async (targetLocale) => COLLOCATION_LOCALES.has(targetLocale),
  },
  "spoken-numbers": {
    slug: "spoken-numbers",
    titleKey: "activities.spoken-numbers.title",
    descriptionKey: "activities.spoken-numbers.description",
    icon: "payments",
    // The second listening activity, and it shares the color with Ear Training on
    // purpose: both are ear work. The catalog is a 2-column grid, so at these two
    // positions (7th and 10th) the cards sit diagonally, never touching - which is
    // the rule the ActivityAccent comment above is protecting.
    accent: "listening",
    href: "/activities/spoken-numbers",
    status: "active",
    availableFor: async (targetLocale) => SPOKEN_NUMBER_LOCALES.has(targetLocale),
  },
  "speed-listening": {
    slug: "speed-listening",
    titleKey: "activities.speed-listening.title",
    descriptionKey: "activities.speed-listening.description",
    icon: "speed",
    // The third listening activity, and its position here is deliberate. The catalog is
    // a 2-column grid, so this entry (11th) sits diagonally from spoken-numbers (10th)
    // and two rows below minimal-pairs (7th): three listening cards, none touching,
    // which is the rule the ActivityAccent comment above is protecting. Appending it at
    // the end instead would have parked it directly under spoken-numbers.
    accent: "listening",
    href: "/activities/speed-listening",
    status: "active",
    // No corpus and no *_LOCALES set: the script is generated per round, so any target
    // locale the model writes works. Same reasoning as delayed-recall.
    availableFor: async () => true,
  },
  "reading-fluency": {
    slug: "reading-fluency",
    titleKey: "activities.reading-fluency.title",
    descriptionKey: "activities.reading-fluency.description",
    icon: "speed",
    // Reading is the skill, outright. Shares the color with phrase-chunks and
    // collocations, which sit far away in the grid.
    accent: "reading",
    href: "/activities/reading-fluency",
    status: "active",
    // No corpus: the passage is generated per sprint, so any locale the model writes
    // works. Same reasoning as scenarios and delayed-recall.
    availableFor: async () => true,
  },
  shadowing: {
    slug: "shadowing",
    titleKey: "activities.shadowing.title",
    descriptionKey: "activities.shadowing.description",
    icon: "graphic_eq",
    // Shares speaking with voice-capsule: both are the learner's own voice. In the
    // 2-column catalog they sit at positions 3 and 12, nowhere near each other.
    accent: "speaking",
    href: "/activities/shadowing",
    status: "active",
    availableFor: async (targetLocale) => SHADOWING_LOCALES.has(targetLocale),
  },
  "real-speech": {
    slug: "real-speech",
    titleKey: "activities.real-speech.title",
    descriptionKey: "activities.real-speech.description",
    // Speech goes in, text comes out. That is the activity, and the glyph says so.
    icon: "speech_to_text",
    // Shares writing with conjugation-gym: both are graded on the learner producing
    // a correct STANDARD written form. The ear colors (listening) went to Ear
    // Training and Spoken Numbers, and in the 2-column catalog this card sits
    // directly under Spoken Numbers, so taking listening here would put two of them
    // edge to edge. conjugation-gym is at position 6 and this is at 12: never touching.
    accent: "writing",
    href: "/activities/real-speech",
    status: "active",
    availableFor: async (targetLocale) => REAL_SPEECH_LOCALES.has(targetLocale),
  },
  circumlocution: {
    slug: "circumlocution",
    titleKey: "activities.circumlocution.title",
    descriptionKey: "activities.circumlocution.description",
    // A detour sign: the whole activity is taking the other road to the meaning
    // when the direct one (the word) is closed.
    icon: "alt_route",
    // Shares writing with conjugation-gym: both are typed production. In the
    // 2-column catalog they sit at positions 6 and 12, three rows apart in the
    // same column, so the two cards never touch.
    accent: "writing",
    href: "/activities/circumlocution",
    status: "active",
    availableFor: async (targetLocale) => CIRCUMLOCUTION_LOCALES.has(targetLocale),
  },
  "message-decoder": {
    slug: "message-decoder",
    titleKey: "activities.message-decoder.title",
    descriptionKey: "activities.message-decoder.description",
    icon: "sms",
    // Shares coral with scenarios, and it is the one pairing that is about meaning
    // rather than convenience: both activities are real conversation, one spoken and
    // one written. In the 2-column catalog they sit at positions 1 and 12, which is
    // as far apart as the grid goes.
    accent: "coral",
    href: "/activities/message-decoder",
    status: "active",
    availableFor: async (targetLocale) => MESSAGE_DECODER_LOCALES.has(targetLocale),
  },
  "predictive-listening": {
    slug: "predictive-listening",
    titleKey: "activities.predictive-listening.title",
    descriptionKey: "activities.predictive-listening.description",
    icon: "online_prediction",
    // NOT the listening color, even though this is ear work, and the reason is the rule
    // in the ActivityAccent comment above: in the 2-column grid this card lands twelfth,
    // directly under spoken-numbers (tenth) and beside shadowing (eleventh), so it may
    // not take `listening` or `speaking` without two same-colored cards touching. It
    // shares lavender with teach-back (2nd) and delayed-recall (8th), which are nowhere
    // near it - and which it does have something in common with: all three are about what
    // the learner already knows, rather than about the signal arriving at their ear.
    accent: "lavender",
    href: "/activities/predictive-listening",
    status: "active",
    // No corpus: every scene is generated per round and stored on the row that grades it,
    // so any target locale the model can write and the synthesiser can speak works. Same
    // reasoning as scenarios and delayed-recall, hence no *_LOCALES set above.
    availableFor: async () => true,
  },
  "word-order": {
    slug: "word-order",
    titleKey: "activities.word-order.title",
    descriptionKey: "activities.word-order.description",
    icon: "reorder",
    // Shares writing with conjugation-gym: both drill production of the grammar
    // itself. In the 2-column catalog they land at positions 6 and 12: same
    // column, three rows apart, so the two cards never touch.
    accent: "writing",
    href: "/activities/word-order",
    status: "active",
    availableFor: async (targetLocale) => WORD_ORDER_LOCALES.has(targetLocale),
  },
  "grammar-forks": {
    slug: "grammar-forks",
    titleKey: "activities.grammar-forks.title",
    descriptionKey: "activities.grammar-forks.description",
    // A fork in the road, which is the whole metaphor.
    icon: "alt_route",
    // Shares writing with conjugation-gym, and of all the deliberate pairings this
    // is the one with the strongest claim: they are the same instrument. Both are
    // timed retrieval gyms, both report "automatic %" rather than accuracy, and
    // both exist because knowing a rule and having it are different things. In the
    // 2-column catalog they sit at positions 6 and 12, diagonally apart.
    accent: "writing",
    href: "/activities/grammar-forks",
    status: "active",
    availableFor: async (targetLocale) => GRAMMAR_FORK_LOCALES.has(targetLocale),
  },
};

export function activityHref(slug: ActivitySlug): string {
  return ACTIVITIES[slug].href;
}

/** Active + available entries, in registry (display) order. */
export async function activeActivities(targetLocale: string): Promise<ActivityDefinition[]> {
  const defs = Object.values(ACTIVITIES).filter((a) => a.status === "active");
  const availability = await Promise.all(defs.map((a) => a.availableFor(targetLocale)));
  return defs.filter((_, i) => availability[i]);
}
